import React, {useRef, useState} from 'react';
import {Button, Empty, Skeleton, Tooltip} from "antd";
import {useParams} from "react-router-dom";
import {useApiSearchAdminPollQuestionSurveyIdResult} from "@/api/question";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import toFaNumber from "@/lib/utils/number";
import {useReactToPrint} from "react-to-print";
import {PrinterIcon} from "lucide-react";
import {useAppTheme} from "@/provider/ThemeProvider";

function QuestionWithResultSurveySection() {
    const param = useParams()
    const survey_id = param.id;
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);

    const contentRef = useRef(null);
    const reactToPrintFn = useReactToPrint({contentRef});

    const {isDark} = useAppTheme();
    const {data, isPending: isPendingGet, refetch, isRefetching} = useApiSearchAdminPollQuestionSurveyIdResult({
        survey_id: survey_id,
        question_type: 'choice',
        page: currentPage,
        per_page: pageSize
    });

    const renderChart = (question) => {
        if (question.type === 'text') {
            return (
                <div className="mt-4 p-4 rounded">
                    <p className="text-gray-600">تعداد پاسخ‌ها: {question.answer_details_count}</p>
                </div>
            );
        }

        const chartData = question.options.map(option => ({
            name: option.title,
            y: option.answer_details_count,
            score: option.score
        }));

        const total = chartData.reduce((sum, item) => sum + item.y, 0);

        const options = {
            chart: {
                type: 'column',
                height: 300,
                backgroundColor: isDark ? '#141414' : '#ffffff',
                plotBackgroundColor: isDark ? '#141414' : '#ffffff',

                style: {
                    fontFamily: 'Vazirmatn, sans-serif'
                }
            },
            title: {
                text: null
            },
            xAxis: {
                categories: chartData.map(item => item.name),
                lineColor: isDark ? '#4b5563' : '#d9d9d9',
                tickColor: isDark ? '#4b5563' : '#d9d9d9',
                labels: {
                    style: {
                        color: isDark ? '#d1d5db' : '#595959',
                        fontFamily: 'Vazirmatn, sans-serif'
                    }
                }
            },
            yAxis: {
                title: {
                    text: 'تعداد پاسخ‌ها',
                    style: {
                        color: isDark ? '#d1d5db' : '#595959',
                        fontFamily: 'Vazirmatn, sans-serif'
                    }
                },
                labels: {
                    style: {
                        fontFamily: 'Vazirmatn, sans-serif'
                    }
                },
                lineColor: isDark ? '#4b5563' : '#d9d9d9',
                tickColor: isDark ? '#4b5563' : '#d9d9d9',
                gridLineColor: isDark ? '#303030' : '#f0f0f0',
            },
            // tooltip: {
            //     headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
            //     pointFormat:
            //         '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
            //         '<td style="padding:0"><b>{point.y} ( {point.percentage:.1f}% )</b></td></tr>',
            //     footerFormat: '</table>',
            //     shared: true,
            //     useHTML: true,
            //     style: {
            //         fontFamily: 'Vazirmatn, sans-serif'
            //     }
            // },
            plotOptions: {

                column: {
                    pointPadding: 0.2,
                    borderWidth: 0,
                    colorByPoint: true,
                    colors: ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1'],
                    dataLabels: {
                        enabled: true,
                        formatter: function () {
                            const count = this.y;
                            const percent = ((this.y / total) * 100).toFixed(1);
                            return `${count}  ( ${percent}% )`;
                        },
                        style: {
                            fontFamily: 'Vazirmatn, sans-serif',
                            textOutline: 'none',
                            fontSize: '12px'
                        }
                    }
                }
            },
            series: [
                {
                    name: 'تعداد پاسخ‌ها',
                    data: chartData.map(item => item.y),
                    showInLegend: false
                }
            ],
            credits: {
                enabled: false
            }
        };

        return (
            <div className="mt-4">
                <HighchartsReact
                    highcharts={Highcharts}
                    options={options}
                />
            </div>
        );
    };

    const renderQuestionType = (type) => {
        switch (type) {
            case 'single_choice':
                return <span className="text-blue-500">تک انتخابی</span>;
            case 'multiple_choice':
                return <span className="text-green-500">چند انتخابی</span>;
            case 'text':
                return <span className="text-purple-500">تشریحی</span>;
            default:
                return type;
        }
    };

    if (isPendingGet) {
        return <Skeleton active/>;
    }

    return (
        <>
            <Button
                style={{width: '50px'}}
                className="mb-4 absolute left-2 mt-4"

                onClick={reactToPrintFn}>
                <PrinterIcon/>
            </Button>
            <div ref={contentRef}>

                {data?.data?.data?.length <= 0 && <Empty description={'داده‌ای یافت نشد'}/>}
                <div className="space-y-6">


                    {data?.data?.data?.map((question) => (
                        <div key={question.id} className="p-6 rounded-lg shadow">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h3 className="text-lg font-semibold mb-2">
                                        {question.title}
                                        {question.required ? (
                                            <span className="ml-2 text-xs mx-2 text-red-500">(الزامی)</span>
                                        ) : ''}
                                        {question.for_teacher ? (

                                            <Tooltip
                                                title={'این سوال به ازای همه اساتیدی که در دریافت این گواهی نامه برای پرونده فراگیر ثبت شده است تکرار شده'}>
                                            <span className="ml-2 text-xs mx-2 text-green-500">
                                        (سوال از استاد)
                                                </span>
                                            </Tooltip>

                                        ) : ''}

                                    </h3>
                                    {question.description && (
                                        <p className="text-gray-600 dark:text-gray-400 mb-4">{question.description}</p>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-500">نوع سوال:</span>
                                        {renderQuestionType(question.type)}
                                    </div>
                                </div>
                                <div className="text-sm text-gray-500">
                                    تعداد پاسخ‌ها:
                                    <span
                                        className={'font-bold mx-2 text-blue-600'}>{toFaNumber(question.answer_details_count)}</span>
                                </div>
                            </div>

                            {renderChart(question)}
                        </div>
                    ))}

                    {/*{data?.meta && (*/}
                    {/*    <div className="flex justify-center mt-6">*/}
                    {/*        <Pagination*/}
                    {/*            current={currentPage}*/}
                    {/*            pageSize={pageSize}*/}
                    {/*            total={data.meta.total}*/}
                    {/*            onChange={(page, pageSize) => {*/}
                    {/*                setCurrentPage(page);*/}
                    {/*                setPageSize(pageSize);*/}
                    {/*            }}*/}
                    {/*            showSizeChanger*/}
                    {/*            showTotal={(total, range) => `${range[0]}-${range[1]} از ${total} سوال`}*/}
                    {/*        />*/}
                    {/*    </div>*/}
                    {/*)}*/}
                </div>
            </div>
        </>
    );
}

export default QuestionWithResultSurveySection;


